/**
 * RtlResultsColumn — the priced RTL/GUC scenario, as the workbench's RIGHT COLUMN.
 *
 * The form stays where it is on the left and the answer builds beside it. That
 * is the layout this screen shipped with, and the one it is back to: pricing a
 * scenario is an ITERATE loop (change leverage, re-price, look again), and the
 * full-width takeover surface — form folded into a summary bar on top, results
 * across the page — put the inputs a fold-and-scroll away from the number they
 * produce. `RtlResultsView` still exists for the wide read; this is what the
 * sizer renders.
 *
 * Card order is the desk's reading order, top to bottom:
 *   1. the rate, dark, with the additive build-up beneath it
 *   2. leverage requested vs max
 *   3. borrower classification and the provenance of what resolved it
 *   4. reserves and coverage
 *   5. the market that priced it
 *   6. guideline check / ladder / caveats
 *
 * RENDER-ONLY, same rule as `ResultsPanel`, whose field mapping it keeps
 * verbatim: every number below is a field of `POST /pricing/scenario/rtl`.
 * Nothing here derives a rate, a leverage maximum, or an eligibility.
 */
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { fmtMoney, fmtPct, fmtRate, fmtAdjPct, humanize, resolvedSource } from "../sizerFormat";
import { SZ, ResultStack } from "../../sizersShared/sizerUi";
import {
  Callout,
  GuidelineCheckList,
  ClosingCostLadder,
  PayloadWarnings,
  SubjectToChangeNote,
  ScenarioDisclaimer,
  ClassExplanation,
} from "../../sizersShared/sizerResultUi";
import { rtlBuyRate, rtlRequiredCoupon } from "../../sizersShared/rtlRateDisplay";
import { status as STATUS } from "../../../theme/colors";

/* ------------------------------------------------------------------ atoms */

const Card = ({ pad = true, children, sx }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      ...(pad ? { p: "15px 17px" } : null),
      ...sx,
    }}
  >
    {children}
  </Box>
);

const CardTitle = ({ children, right }) => (
  <Flx jb ac g={1} sx={{ mb: 1.4 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: SZ.ink400,
      }}
    >
      {children}
    </Txt>
    {right}
  </Flx>
);

const Mono = ({ children, sx }) => (
  <Box
    component="span"
    sx={{ fontFamily: SZ.mono, fontVariantNumeric: "tabular-nums", ...sx }}
  >
    {children}
  </Box>
);

/** Dashed key/value row. `src` is the small provenance word after the value. */
const Row = ({ k, v, src, tone, last }) => (
  <Flx
    jb
    g={1}
    sx={{
      alignItems: "baseline",
      py: "6px",
      borderBottom: last ? "none" : `1px dashed ${SZ.line}`,
    }}
  >
    <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>{k}</Txt>
    <Box sx={{ textAlign: "right" }}>
      <Mono
        sx={{
          fontSize: 12.5,
          fontWeight: tone ? 600 : 400,
          color: tone === "pos" ? SZ.good : tone === "neg" ? SZ.bad : SZ.ink900,
        }}
      >
        {v}
      </Mono>
      {src && (
        <Txt component="span" sx={{ fontSize: 10, color: SZ.ink400, ml: 0.75 }}>
          {src}
        </Txt>
      )}
    </Box>
  </Flx>
);

/* ------------------------------------------------------------------ cards */

/**
 * The rate, and the sum that produced it. Dark because it is the one figure on
 * the page that is the ANSWER — the white cards under it are the working.
 * Headline = the required coupon (what the borrower pays); the buy rate is the
 * required base, below the coupon by the account spread (owner 2026-10-08).
 */
/** The rate build-up chips (Base / Term / Class / Amount / Leverage / Spread). */
export const rtlRateChips = (rate = {}) =>
  [
    { k: "Base", v: fmtRate(rate.baseRate12mo), on: rate.baseRate12mo != null },
    { k: "Term", v: fmtAdjPct(rate.termAdjPct), on: rate.termAdjPct != null },
    { k: "Class", v: fmtAdjPct(rate.classAdjPct), on: rate.classAdjPct != null },
    {
      k: rate.uptickTier ? `Amount ${rate.uptickTier}` : "Amount",
      v: fmtAdjPct(rate.loanAmountUptickPct),
      on: rate.loanAmountUptickPct != null,
    },
    {
      k: rate.leverageBand ? `Leverage ${rate.leverageBand}` : "Leverage",
      v: fmtAdjPct(rate.leverageAdjPct),
      on: rate.leverageAdjPct != null,
    },
    { k: "Spread", v: fmtRate(rate.accountSpread), on: rate.accountSpread != null },
  ].filter((c) => c.on);

const RateHero = ({ rate, termMonths }) => {
  const chips = rtlRateChips(rate);

  return (
    <Card pad={false} sx={{ border: 0, overflow: "hidden" }}>
      <Box sx={{ background: SZ.heroBg, color: "#fff", p: "18px 17px 15px" }}>
        <Txt
          component="div"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: ".09em",
            textTransform: "uppercase",
            color: SZ.heroMuted,
          }}
        >
          Required coupon
        </Txt>
        <Txt
          component="div"
          sx={{
            fontFamily: SZ.mono,
            fontSize: 32,
            fontWeight: 600,
            letterSpacing: "-.02em",
            fontVariantNumeric: "tabular-nums",
            m: "4px 0 1px",
          }}
        >
          {fmtRate(rtlRequiredCoupon(rate))}
        </Txt>
        <Txt component="div" sx={{ fontSize: 12, color: SZ.heroMuted }}>
          {rtlBuyRate(rate) != null && (
            <>
              Buy rate{" "}
              <Mono sx={{ color: "#fff", fontWeight: 500 }}>{fmtRate(rtlBuyRate(rate))}</Mono>
              {termMonths ? " · " : ""}
            </>
          )}
          {termMonths ? `${termMonths} mo` : ""}
        </Txt>
      </Box>
      {chips.length > 0 && (
        <Flx g={0.75} sx={{ background: SZ.heroPanel, p: "11px 15px 13px", flexWrap: "wrap" }}>
          {chips.map((c) => (
            <Txt
              key={c.k}
              sx={{
                fontFamily: SZ.mono,
                fontSize: 10.5,
                color: SZ.heroChipInk,
                background: SZ.heroChip,
                borderRadius: "5px",
                p: "3px 7px",
                whiteSpace: "nowrap",
              }}
            >
              {c.k}{" "}
              <Box component="span" sx={{ color: "#fff" }}>
                {c.v}
              </Box>
            </Txt>
          ))}
        </Flx>
      )}
    </Card>
  );
};

/** Requested vs maximum, per metric. `over` is the engine's own flag. */
const LeverageCard = ({ leverage = [], reductions = [] }) => {
  if (!leverage.length) return null;
  /* The top-line count, required by both specs: per-row "Over" chips make each
     breach findable but not the SHAPE of the failure — a GUC scenario over on
     all four caps reads the same as one over on LTARV alone until you compare
     four rows. The summary states it once, at a glance. */
  const over = leverage.filter((r) => r.over).length;
  const th = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: SZ.ink400,
    textAlign: "right",
    padding: "0 0 7px",
    borderBottom: `1px solid ${SZ.line}`,
  };
  const td = {
    padding: "7px 0",
    borderBottom: `1px dashed ${SZ.line}`,
    fontFamily: SZ.mono,
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    fontSize: 12.5,
  };
  return (
    <Card>
      <CardTitle
        right={
          over > 0 ? (
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: SZ.bad,
                background: SZ.badBg,
                borderRadius: "5px",
                p: "2px 8px",
                whiteSpace: "nowrap",
              }}
            >
              {over} cap{over === 1 ? "" : "s"} exceeded
            </Txt>
          ) : null
        }
      >
        Leverage — requested vs max
      </CardTitle>
      {over > 0 && (
        <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink500, mt: "-6px", mb: 1.25 }}>
          Restructure or request an exception — the caps below are the reduced
          maximums, market reductions included.
        </Txt>
      )}
      {/* A fixed 3-column comparison of four known metrics, not a data grid —
          nothing to sort, filter or navigate, so AG Grid has no job here. */}
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ ...th, textAlign: "left" }}>
              Metric
            </Box>
            <Box component="th" sx={th}>
              Requested
            </Box>
            <Box component="th" sx={th}>
              Max
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {leverage.map((r, i) => (
            <Box component="tr" key={r.metric || i}>
              <Box
                component="td"
                sx={{
                  ...td,
                  fontFamily: "inherit",
                  textAlign: "left",
                  fontWeight: 500,
                  color: SZ.ink500,
                }}
              >
                {r.metric}
              </Box>
              <Box component="td" sx={{ ...td, color: r.over ? SZ.bad : SZ.ink900 }}>
                {fmtPct(r.requested, 2)}
                {r.over && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "inherit",
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      color: SZ.bad,
                      background: SZ.badBg,
                      borderRadius: "4px",
                      p: "1px 5px",
                      ml: 0.75,
                    }}
                  >
                    Over
                  </Box>
                )}
              </Box>
              <Box component="td" sx={{ ...td, color: r.over ? SZ.bad : SZ.ink900 }}>
                {r.maximum == null ? "—" : fmtPct(r.maximum, 2)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {/* §5.1 / §5.6: a blue pill + the itemized reductions. `maximum` above is
          already post-reduction — nothing is subtracted here. */}
      {reductions.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${SZ.line}` }}>
          <Box
            component="span"
            sx={{
              display: "inline-block",
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: STATUS.info.fg,
              background: STATUS.info.bg,
              borderRadius: "99px",
              p: "2px 9px",
              mb: 0.75,
            }}
          >
            {reductions.length} leverage reduction{reductions.length === 1 ? "" : "s"} applied
          </Box>
          {reductions.map((r, i) => (
            <Flx key={r.code || i} jb g={1} sx={{ alignItems: "baseline", py: "4px" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Txt component="div" sx={{ fontSize: 12, color: SZ.ink700 }}>
                  {r.reason}
                </Txt>
                {r.citation && (
                  <Txt component="div" sx={{ fontSize: 10.5, color: SZ.ink400 }}>
                    {r.citation}
                  </Txt>
                )}
              </Box>
              <Mono sx={{ fontSize: 12, fontWeight: 600, color: SZ.warn }}>
                {fmtPct(r.reduction, 2)}
              </Mono>
            </Flx>
          ))}
          <Txt component="div" sx={{ fontSize: 11, color: SZ.ink400, mt: 0.5 }}>
            The maximums above already include these reductions.
          </Txt>
        </Box>
      )}
    </Card>
  );
};

/**
 * "What fits" (pricing-scenario-sizers.md §6, `suggestions[]`): one row per
 * over metric at its reduced maximum, then up to two PASSING alternatives, each
 * a one-click re-price at its `baseLoanAmount`. Never computed here — the
 * engine's amounts already net out the holdback and the reductions.
 */
const WhatFitsCard = ({ suggestions = [], onRepriceAt }) => {
  const metricRows = suggestions.filter((s) => s.metric);
  const altRows = suggestions.filter((s) => s.type);
  if (!metricRows.length && !altRows.length) return null;
  return (
    <Card>
      <CardTitle>What fits</CardTitle>
      {metricRows.map((r) => (
        <Txt key={r.metric} component="div" sx={{ fontSize: 12.5, color: SZ.ink700, py: "4px" }}>
          <Box component="span" sx={{ fontWeight: 600, color: SZ.ink900 }}>
            {r.metric}:
          </Box>{" "}
          max {fmtMoney(r.maxLoanAtMax)} total ({fmtMoney(r.baseLoanAmountAtMax)} base) at{" "}
          {fmtPct(r.feasibleLeverage, 1)}
        </Txt>
      ))}
      {altRows.map((r) => (
        <Flx
          key={r.type}
          jb
          ac
          g={1}
          sx={{
            mt: 1,
            p: "9px 11px",
            border: `1px solid ${SZ.line}`,
            borderRadius: SZ.radiusSm,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 180px" }}>
            <Txt component="div" sx={{ fontSize: 12.5, color: SZ.ink900 }}>
              {humanize(r.type)} — {fmtMoney(r.baseLoanAmount)} base · {fmtMoney(r.totalLoanAmount)} total
            </Txt>
            <Txt component="div" sx={{ fontSize: 11.5, color: r.passing ? SZ.good : SZ.warn }}>
              {r.passing ? "Measured as passing" : "Measured as still over"}
            </Txt>
          </Box>
          {onRepriceAt && (
            <Box
              component="button"
              type="button"
              onClick={() => onRepriceAt(r.baseLoanAmount)}
              sx={{
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 600,
                color: SZ.teal700,
                background: SZ.card,
                border: `1px solid ${SZ.teal100}`,
                borderRadius: "6px",
                p: "5px 10px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": { background: SZ.teal50 },
              }}
            >
              Reprice at this amount
            </Box>
          )}
        </Flx>
      ))}
    </Card>
  );
};

/**
 * The class, the two point pools that made it, and the provenance of the three
 * resolved inputs. The split bar is the SCORE, not a percentage of anything —
 * credit points and experience points out of the engine's 10.
 */
const ClassificationCard = ({ result }) => {
  const resolved = result.resolved || {};
  const cl = resolved.classification || {};
  const credit = Number(cl.creditScore) || 0;
  const exp = Number(cl.experienceScore) || 0;
  const total = cl.totalScore ?? credit + exp;
  const cls = resolved.borrowerClass || cl.borrowerClass;
  const tone =
    cls === "B"
      ? { fg: SZ.warn, bg: SZ.warnBg, br: `${SZ.warn}44` }
      : cls === "C"
        ? { fg: SZ.bad, bg: SZ.badBg, br: `${SZ.bad}44` }
        : { fg: SZ.teal700, bg: SZ.teal50, br: SZ.teal100 };

  return (
    <Card>
      <CardTitle>Borrower classification</CardTitle>
      <Flx ac g={1.25} sx={{ mb: 1.4, flexWrap: "wrap" }}>
        <Txt
          sx={{
            fontFamily: SZ.mono,
            fontSize: 14,
            fontWeight: 600,
            color: tone.fg,
            background: tone.bg,
            border: `1px solid ${tone.br}`,
            borderRadius: "7px",
            p: "4px 11px",
          }}
        >
          Class {cls || "—"}
        </Txt>
        <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>
          Total score <Mono sx={{ color: SZ.ink900, fontWeight: 600 }}>{total}</Mono> / 10
        </Txt>
        {resolved.isForeignNational && (
          <Txt sx={{ fontSize: 11, color: SZ.warn }}>Foreign national</Txt>
        )}
      </Flx>

      <Flx
        sx={{
          height: 8,
          borderRadius: "99px",
          overflow: "hidden",
          background: SZ.bg,
          border: `1px solid ${SZ.line}`,
          mb: 1,
        }}
      >
        <Box sx={{ width: `${Math.min(credit, 10) * 10}%`, background: SZ.teal700 }} />
        <Box sx={{ width: `${Math.min(exp, 10) * 10}%`, background: SZ.scoreExp }} />
      </Flx>
      <Flx g={2} sx={{ mb: 1.25 }}>
        {[
          { c: SZ.teal700, label: "Credit", n: credit },
          { c: SZ.scoreExp, label: "Experience", n: exp },
        ].map((l) => (
          <Flx key={l.label} ac g={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: l.c }} />
            <Txt sx={{ fontSize: 11, color: SZ.ink400 }}>
              {l.label} <Mono sx={{ color: SZ.ink700, fontWeight: 600 }}>{l.n}</Mono>
            </Txt>
          </Flx>
        ))}
      </Flx>

      <Row
        k="Resolved FICO"
        v={resolved.fico?.value ?? "—"}
        src={resolvedSource(resolved.fico)}
      />
      <Row
        k="Cumulative experience"
        v={resolved.experience?.value ?? "—"}
        src={resolvedSource(resolved.experience)}
      />
      <Row
        k="Resolved liquidity"
        v={fmtMoney(resolved.liquidity?.value)}
        src={resolvedSource(resolved.liquidity)}
        last
      />
      <ClassExplanation text={result.classExplanation} />
    </Card>
  );
};

/**
 * Months of payment coverage priced at the adjusted rate through the canonical
 * per-diem engine. `requiredLiquidity` is the only one of these that maps to a
 * real UW field — the rest are response-only derived values.
 */
const ReservesCard = ({ req }) => {
  if (req.reserveMonths == null) return null;
  const c = req.liquidityComponents || {};
  // The cash uses the guideline sums (§12b): only the BORROWER-CASH side of
  // closing costs and the interest reserve is a liquidity use.
  const parts = [
    ["Months of payments", c.monthsPayments],
    ["Non-financed construction", c.nonFinancedConstruction],
    ["Down payment", c.downPayment],
    [
      "Closing costs (borrower cash)",
      c.closingCosts,
      c.closingCostsBasis && c.closingCostsBasis !== "entered" ? "estimated" : null,
    ],
    ["Interest reserve (borrower cash)", c.borrowerFundedInterestReserve],
  ].filter(([, v]) => Number(v) > 0);
  return (
    <Card>
      <CardTitle>Reserves &amp; coverage</CardTitle>
      <Row k="Reserve months" v={String(req.reserveMonths)} />
      <Row k="Monthly interest" v={fmtMoney(req.monthlyInterestPayment)} />
      {parts.map(([k, v, src]) => (
        <Row key={k} k={k} v={fmtMoney(v)} src={src} />
      ))}
      <Row k="Required liquidity" v={fmtMoney(req.requiredLiquidity)} />
      {req.liquidityCovered != null && (
        <Row
          k="Liquidity Coverage"
          v={req.liquidityCovered ? "Sufficient Liquidity" : `Shortfall ${fmtMoney(req.liquidityShortfall)}`}
          tone={req.liquidityCovered ? "pos" : "neg"}
          last
        />
      )}
      {req.paymentCoverage && (
        <Txt sx={{ fontSize: 11, color: SZ.ink400, mt: 0.75, fontStyle: "italic" }}>
          {req.paymentCoverage}
        </Txt>
      )}
    </Card>
  );
};

/** Sparkline over the ZHVI series. Presentation only — no derivation. */
const Spark = ({ values }) => {
  if (!values || values.length < 2) return null;
  const W = 320;
  const H = 44;
  const pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => [
    pad + (i / (values.length - 1)) * (W - pad * 2),
    H - pad - ((v - min) / span) * (H - pad * 2),
  ]);
  const d = "M" + pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" L");
  const lastPt = pts[pts.length - 1];
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Home value trend"
      sx={{ width: "100%", height: 44, display: "block", my: "6px" }}
    >
      <path d={d} fill="none" stroke={SZ.teal600} strokeWidth={1.6} strokeLinejoin="round" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r={2.4} fill={SZ.teal600} />
    </Box>
  );
};

/**
 * The market read the price was made against — `marketAssessment` from the
 * response, with the ZHVI series it resolved from. No coverage is not an error:
 * it is a stated gap, because the market-driven leverage flags simply never
 * fired for this scenario.
 */
const MarketCard = ({ assessment, marketData }) => {
  const zhvi = marketData?.zhvi;
  const values = (zhvi?.timeSeries || []).map((p) => p?.value).filter((v) => v != null);
  const found = assessment && assessment.metroStatus === "found";

  if (!found) {
    return (
      <Card>
        <CardTitle>Market data</CardTitle>
        <Callout tone="warn" title="No market data for this ZIP">
          Neither HouseCanary nor ZHVI has coverage here — the market-driven
          leverage flags did not run for this scenario.
        </Callout>
      </Card>
    );
  }

  const flags = assessment.guidelineFlags || [];
  return (
    <Card>
      <CardTitle
        right={
          assessment.trend ? (
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                color: SZ.teal700,
                background: SZ.teal50,
                borderRadius: "5px",
                p: "2px 8px",
              }}
            >
              {humanize(assessment.trend)}
            </Txt>
          ) : null
        }
      >
        Market{assessment.zipCode ? ` — ZIP ${assessment.zipCode}` : ""}
      </CardTitle>
      <Row k="Median value (ZHVI)" v={fmtMoney(assessment.medianValue ?? zhvi?.latestZHVI)} />
      {assessment.hpaYoY != null && <Row k="HPA YoY" v={fmtPct(assessment.hpaYoY, 2)} />}
      {assessment.hpa3mAnnualized != null && (
        <Row k="HPA 3-mo (ann.)" v={fmtPct(assessment.hpa3mAnnualized, 2)} />
      )}
      <Spark values={values.slice(-60)} />
      {assessment.subjectValue != null && (
        <Row k="Subject value" v={fmtMoney(assessment.subjectValue)} />
      )}
      {assessment.zillowMultiplier != null && (
        <Row
          k="ZHVI multiplier"
          v={`${Number(assessment.zillowMultiplier).toFixed(2)}×`}
          last={!assessment.ruralSignal && !flags.length}
        />
      )}
      {assessment.ruralSignal && (
        <Txt sx={{ fontSize: 11.5, color: SZ.warn, mt: 0.75 }}>• Rural signal</Txt>
      )}
      {flags.map((f, i) => (
        <Txt key={i} sx={{ fontSize: 11.5, color: SZ.warn, mt: 0.5 }}>
          • {f.reason}
          {f.reduction != null ? ` (−${fmtPct(f.reduction, 2)})` : ""}
        </Txt>
      ))}
    </Card>
  );
};

/** §5.6: what makes the red "Hard exception" banner. */
// Follows the BACKEND's own answers rather than raw lists: `eligible` is false
// on a hard guideline row or a facility `is_hard_exception` (pricing_engine),
// and a takeout denial only blocks in GATE mode — in advisory mode (no takeout
// declared) the engine calls it information, so it must not raise this banner.
const hasHardException = (result) =>
  result?.eligible === false ||
  (result?.guidelineExceptions || []).some((e) => e?.severity === "hard") ||
  Boolean(result?.facilityHardExceptions?.is_hard_exception) ||
  (result?.guidelineCheck?.mode === "gate" &&
    (result?.guidelineCheck?.takeout_denials || []).length > 0);

/* ------------------------------------------------------------------ column */

// `hideHero` drops the rate card — the scenario workspace's Review mode shows
// the rate in its KPI strip instead.
// `onRequestException` / `pendingCodes` put Request exception on failing
// guideline rows; `onRepriceAt` makes "What fits" alternatives one-click;
// `onExplain` adds "Review findings" to the hard-exception banner.
export default function RtlResultsColumn({
  result,
  pending,
  error,
  masonry,
  hideHero,
  onRequestException,
  pendingCodes,
  onRepriceAt,
  onExplain,
}) {
  if (error) {
    return (
      <Card>
        <Flx g={1} sx={{ alignItems: "flex-start" }}>
          <ErrorOutlineRoundedIcon sx={{ color: SZ.bad, fontSize: 18 }} />
          <Txt sx={{ fontSize: 13, color: SZ.bad }}>
            {error.message || "Failed to price this loan."}
          </Txt>
        </Flx>
      </Card>
    );
  }

  if (pending && !result) {
    return (
      <Card>
        <Flx center g={1.5} sx={{ py: 4 }}>
          <CircularProgress size={20} sx={{ color: SZ.teal700 }} />
          <Txt sx={{ fontSize: 13, color: SZ.ink500 }}>Calculating…</Txt>
        </Flx>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card sx={{ p: "30px 20px", textAlign: "center" }}>
        <Flx
          center
          sx={{
            width: 40,
            height: 40,
            borderRadius: SZ.radius,
            background: SZ.teal50,
            color: SZ.teal700,
            m: "0 auto 12px",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
        </Flx>
        <Txt component="div" sx={{ fontSize: 13.5, fontWeight: 600, color: SZ.ink900, mb: 0.5 }}>
          Pricing will appear here
        </Txt>
        <Txt
          component="div"
          sx={{ fontSize: 12, color: SZ.ink400, lineHeight: 1.5, maxWidth: 240, mx: "auto" }}
        >
          Fill out the loan, applicant, and property details, then run Calculate &amp; price.
        </Txt>
      </Card>
    );
  }

  const rate = result.rate || {};
  const req = result.requirements || {};
  // "Clean" is the response saying BOTH things: nothing blocked it, and nothing
  // is still waiting on validation. Either one alone still reads as indicative.
  const clean = result.eligible !== false && !result.subjectToChangePending?.length;

  return (
    <ResultStack masonry={masonry}>
      <PayloadWarnings warnings={result.payloadWarnings} />

      {/* §5.6 DISPLAY TAXONOMY — any hard finding, facility hard exception or
          takeout denial is a red banner with THIS copy. Saving the loan stays
          allowed; processing waits on Account Coverage. */}
      {hasHardException(result) && (
        <Callout tone="bad" title="Hard exception">
          <Box component="span" sx={{ display: "block", lineHeight: 1.55 }}>
            The loan can be created but cannot enter processing until{" "}
            <strong>Account Coverage</strong> approves; requires a documented
            file (at minimum an appraisal).
          </Box>
          {onExplain && (
            <Box
              component="button"
              type="button"
              onClick={onExplain}
              sx={{
                display: "block",
                mt: 1,
                p: 0,
                border: 0,
                background: "none",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {onRequestException &&
              (result.guidelineExceptions || []).some(
                (e) => e?.severity === "hard" || e?.severity === "soft",
              )
                ? "Review findings and request an exception"
                : "Review findings"}
            </Box>
          )}
        </Callout>
      )}

      {hideHero ? null : rate.error ? (
        <Callout tone="warn" title="Rate block unavailable">
          {rate.error}
        </Callout>
      ) : (
        <RateHero rate={rate} termMonths={result.termMonths} />
      )}

      <LeverageCard leverage={result.leverage} reductions={result.leverageReductions || []} />
      <WhatFitsCard suggestions={result.suggestions || []} onRepriceAt={onRepriceAt} />
      <ClassificationCard result={result} />
      <ReservesCard req={req} />
      <MarketCard assessment={result.marketAssessment} marketData={result.marketData} />

      <Card>
        <CardTitle>Guideline check</CardTitle>
        <GuidelineCheckList
          exceptions={result.guidelineExceptions}
          facilityHardExceptions={result.facilityHardExceptions}
          onRequestException={onRequestException}
          pendingCodes={pendingCodes}
        />
      </Card>

      {result.closingCostLadder?.length > 0 && (
        <Card>
          <CardTitle>Closing cost ladder</CardTitle>
          <ClosingCostLadder rows={result.closingCostLadder} />
        </Card>
      )}

      <Card>
        {clean ? (
          <Callout tone="good" title="Eligible — pending validation">
            Every pricing input resolved. The terms firm up as the entered values
            are validated on the loan file.
          </Callout>
        ) : (
          <SubjectToChangeNote
            missingFields={result.missingFields}
            pending={result.subjectToChangePending}
          />
        )}
        <Box sx={{ mt: 1.25 }}>
          <ScenarioDisclaimer text={result.disclaimer} />
        </Box>
      </Card>
    </ResultStack>
  );
}
